import React from "react";
import { useNavigate } from "react-router-dom";
import { useDistanceQueryStore } from "./stores/distanceQueryStore";
import DistanceQueryForm from "./DistanceQueryForm";

// Canonical full-page route (registered top-level so it is reachable from any demo).
export const DISTANCE_QUERY_PATH = "/distance-query";

// Ruler / distance icon
const RulerIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="w-6 h-6"
  >
    <path d="M21.3 8.7 8.7 21.3a1 1 0 0 1-1.4 0l-4.6-4.6a1 1 0 0 1 0-1.4L15.3 2.7a1 1 0 0 1 1.4 0l4.6 4.6a1 1 0 0 1 0 1.4Z" />
    <path d="m7.5 10.5 2 2" />
    <path d="m10.5 7.5 2 2" />
    <path d="m13.5 4.5 2 2" />
    <path d="m4.5 13.5 2 2" />
  </svg>
);

const CloseIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const ExpandIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M15 3h6v6" />
    <path d="M9 21H3v-6" />
    <path d="M21 3l-7 7" />
    <path d="M3 21l7-7" />
  </svg>
);

const DistanceQuery: React.FC = () => {
  const navigate = useNavigate();
  const { isOpen, toggleOpen, setOpen, addHistory } = useDistanceQueryStore();

  const openFullPage = () => {
    setOpen(false);
    navigate(DISTANCE_QUERY_PATH);
  };

  return (
    <>
      {/* Floating button */}
      <button
        type="button"
        onClick={toggleOpen}
        title="Distance query"
        aria-label="Distance query"
        className={`flex items-center justify-center w-12 h-12 rounded-full shadow-lg transition-colors ${
          isOpen
            ? "bg-primary-700 text-white"
            : "bg-primary-500 text-white hover:bg-primary-600"
        }`}
      >
        <RulerIcon />
      </button>

      {/* Modal panel */}
      {isOpen && (
        <div
          className="fixed top-24 right-4 w-80 max-w-[92vw] bg-white rounded-lg shadow-2xl border border-slate-200 z-[100000] flex flex-col"
          style={{ maxHeight: "calc(100vh - 8rem)" }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-primary-500 text-white rounded-t-lg">
            <div className="flex items-center gap-2 min-w-0">
              <RulerIcon />
              <span className="font-semibold text-sm truncate">Distance Query</span>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                type="button"
                onClick={openFullPage}
                className="text-white/90 hover:text-white p-1"
                title="Open full page"
                aria-label="Open full page"
              >
                <ExpandIcon />
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-white/90 hover:text-white p-1"
                aria-label="Close"
              >
                <CloseIcon />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="p-4 overflow-y-auto">
            <DistanceQueryForm onResult={addHistory} datalistId="distance-bays-modal" />
          </div>
        </div>
      )}
    </>
  );
};

export default DistanceQuery;
